import { CandidateRole } from "src/app/models/enum";
import { OBComments, ResetManeuverItem } from "src/app/models/models";

/** Clear text only (keep OB row) vs remove the whole Observation note row. */
export type Ts1SummaryCommentDeleteMode = "clear" | "remove";

/**
 * View/Edit Comments delete rules:
 * - Observed/Unobserved OBs (`action` add/remove) → clear comment text only
 * - General Observations (`isObservationComment`) → remove the whole row
 */
export function getTs1SummaryCommentDeleteMode(comment: OBComments): Ts1SummaryCommentDeleteMode | null {
	if (comment.ob === "REPEAT") return null;
	if (comment.isReset || comment.lastActionDisabled || comment.isDisabled) return null;
	if (comment.isManeuverOutcome || comment.isTemCompetencyOutcome) return null;
	// After clear, hide delete when there is no comment text left.
	if (!comment.instructorComment?.trim()) return null;
	if (comment.isObservationComment) return "remove";
	if (comment.action != null && comment.ob !== "TEM") return "clear";
	return null;
}

export function isTs1SummaryCommentDeletable(comment: OBComments): boolean {
	return getTs1SummaryCommentDeleteMode(comment) != null;
}

/** Sort key: Candidate slot index, then PF before PM. */
export function getTs1SummaryCandidateSortKey(comment: OBComments, candidateIndex: number): string {
	const idx = candidateIndex < 0 ? 999 : candidateIndex;
	const roleRank = comment.candidateRole === CandidateRole.PF ? 0 : comment.candidateRole === CandidateRole.PM ? 1 : 2;
	return `${String(idx).padStart(4, "0")}-${roleRank}`;
}

export function stripRepeatManeuverPrefix(label: string): string {
	return label.replace(/^R\d+\s+/, "").trim();
}

export function repeatNumberForSummaryRow(
	comment: OBComments,
	resetManeuvers: ResetManeuverItem[] = [],
): number | null {
	const scoped = resetManeuvers
		.filter((rm) => {
			if (comment.maneuverCode && rm.maneuverCode !== comment.maneuverCode) return false;
			if (rm.candidateScoped) {
				return !!comment.candidateId && rm.candidateId === comment.candidateId;
			}
			return true;
		})
		.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

	if (!scoped.length) return null;

	const rowTime = comment.createdAt ? new Date(comment.createdAt).getTime() : Number.NaN;
	if (Number.isNaN(rowTime)) {
		return scoped[scoped.length - 1].count;
	}

	const resetsBeforeOrAtRow = scoped.filter((rm) => new Date(rm.createdAt).getTime() <= rowTime);
	if (!resetsBeforeOrAtRow.length) return null;

	return resetsBeforeOrAtRow[resetsBeforeOrAtRow.length - 1].count;
}

export function formatRepeatPrefixedLabel(baseLabel: string, repeatNumber: number | null | undefined): string {
	const stripped = stripRepeatManeuverPrefix(baseLabel);
	if (!repeatNumber || repeatNumber <= 0) return stripped;
	return `R${repeatNumber} ${stripped}`.trim();
}

/** True when group already has this REPEAT attempt (R1 vs R2 are distinct). */
export function groupHasSameRepeatMarker(group: OBComments[], comment: OBComments): boolean {
	return group.some((row) => {
		if (row.ob !== "REPEAT" || row.maneuverCode !== comment.maneuverCode) return false;
		if (row.createdAt && comment.createdAt) return row.createdAt === comment.createdAt;
		return row.maneuver === comment.maneuver;
	});
}

/**
 * Step 3 View/Edit Comments — keep the active carousel candidate's rows plus maneuver-wide REPEAT.
 * Sibling candidate tables are hidden; REPEAT markers stay for timeline context.
 */
export function scopeTs1V3SummaryModalCommentsForReviewCandidate(
	comments: OBComments[],
	activeCandidateId: string,
): OBComments[] {
	if (!activeCandidateId) return comments ?? [];
	return (comments ?? []).filter((comment) => {
		if (comment.ob === "REPEAT") {
			if (comment.candidateId) return comment.candidateId === activeCandidateId;
			return true;
		}
		return comment.candidateId === activeCandidateId;
	});
}

/** Step 2 View/Edit — bucket comments by candidateId; fan REPEAT into every crew seat that repeated the maneuver. */
export function bucketTs1V3SummaryModalCommentsByCandidate(
	comments: OBComments[],
	params: {
		resetManeuvers: ResetManeuverItem[];
		crewCandidateIds: string[];
	},
): { order: string[]; groupsMap: Map<string, OBComments[]> } {
	const { resetManeuvers, crewCandidateIds } = params;
	const groupsMap = new Map<string, OBComments[]>();
	const order: string[] = [];

	const ensureGroup = (candidateId: string): void => {
		if (!groupsMap.has(candidateId)) {
			groupsMap.set(candidateId, []);
			order.push(candidateId);
		}
	};

	const assignRepeat = (comment: OBComments): void => {
		if (!comment.maneuverCode) return;

		if (comment.candidateId) {
			ensureGroup(comment.candidateId);
			const group = groupsMap.get(comment.candidateId)!;
			if (!groupHasSameRepeatMarker(group, comment)) {
				group.push(comment);
			}
			return;
		}

		const maneuverWasRepeated = resetManeuvers.some(
			(rm) => rm.maneuverCode === comment.maneuverCode && !rm.candidateScoped,
		);
		if (!maneuverWasRepeated) return;

		const targetIds = new Set<string>([
			...crewCandidateIds,
			...groupsMap.keys(),
			...resetManeuvers
				.filter((rm) => rm.maneuverCode === comment.maneuverCode && !rm.candidateScoped)
				.map((rm) => rm.candidateId),
		]);

		for (const candidateId of targetIds) {
			if (!candidateId) continue;
			ensureGroup(candidateId);
			const group = groupsMap.get(candidateId)!;
			if (!groupHasSameRepeatMarker(group, comment)) {
				group.push(comment);
			}
		}
	};

	for (const comment of comments ?? []) {
		if (comment.candidateId) {
			ensureGroup(comment.candidateId);
			groupsMap.get(comment.candidateId)!.push(comment);
		} else if (comment.ob === "REPEAT") {
			assignRepeat(comment);
		}
	}

	return { order, groupsMap };
}

export function formatTs1SummaryManeuverLabel(
	comment: OBComments,
	resetManeuvers: ResetManeuverItem[] = [],
	fallbackLabel = "",
): string {
	if (comment.isTemCompetencyOutcome) return "Rating per Competency";
	if (comment.ob === "REPEAT") return comment.maneuver ?? fallbackLabel;

	const base = stripRepeatManeuverPrefix(comment.maneuver ?? fallbackLabel);
	const repeatNumber = repeatNumberForSummaryRow(comment, resetManeuvers);
	if (repeatNumber != null) return formatRepeatPrefixedLabel(base, repeatNumber);
	return base;
}

export function formatTs1SummaryCompetencyStatusLabel(
	comment: OBComments,
	resetManeuvers: ResetManeuverItem[] = [],
): string {
	const code = comment.competencyCode ?? stripRepeatManeuverPrefix(comment.maneuver ?? "");
	const repeatNumber = repeatNumberForSummaryRow(comment, resetManeuvers);
	if (repeatNumber != null) return `R${repeatNumber} ${code} - C`.trim();
	return `${code} - C`;
}

/** Normalize maneuver label on summary rows built from draft/timeline data. */
export function withTs1SummaryManeuverLabel(
	comment: OBComments,
	resetManeuvers: ResetManeuverItem[] = [],
	fallbackLabel = "",
): OBComments {
	return {
		...comment,
		maneuver: formatTs1SummaryManeuverLabel(comment, resetManeuvers, fallbackLabel),
	};
}
